import { useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { dependencyViewerApiRef } from '../api/DependencyViewerApi';
import { DependencyViewerClient } from '../api/DependencyViewerClient';
import {
  Dependency,
  DependencySummary,
  ManifestResult,
  ProgressiveDependencyState,
} from '../utils/types';

export interface UseDependenciesResult {
  /** The current state — available as soon as manifests are parsed */
  data: ProgressiveDependencyState | null;
  /** True only during the initial manifest fetch (before any data is shown) */
  loading: boolean;
  error: Error | null;
  retry: () => void;
}

/** Minimum interval (ms) between React state updates during streaming */
const RENDER_THROTTLE_MS = 400;

/**
 * Hook that progressively loads dependencies using SSE streaming.
 *
 * Flow:
 * 1. Opens an SSE connection to /dependencies/stream
 * 2. Receives skeleton dependencies immediately (with current versions only)
 * 3. Receives batches of resolved versions progressively
 * 4. Throttles React state updates to avoid excessive re-renders
 *
 * The user sees the dependency table almost instantly, and version/status
 * info fills in progressively without blocking the UI.
 */
export function useDependencies(
  projectSlug: string | null,
  manifestPaths?: string[],
): UseDependenciesResult {
  const api = useApi(dependencyViewerApiRef);
  const [data, setData] = useState<ProgressiveDependencyState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Mutable refs for accumulating updates between renders
  const depsRef = useRef<Dependency[]>([]);
  const manifestsRef = useRef<ManifestResult[]>([]);
  const resolvedCountRef = useRef(0);
  const lastRenderRef = useRef(0);
  const pendingRenderRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const retry = useCallback(() => {
    if (projectSlug !== null) {
      api.invalidateCache(projectSlug, manifestPaths);
    }
    setRetryCount(c => c + 1);
  }, [api, projectSlug, manifestPaths]);

  // Stable reference for manifestPaths
  const pathsKey = manifestPaths?.join(',') ?? '';

  useEffect(() => {
    if (projectSlug === null) {
      setLoading(false);
      return;
    }

    // Reset state
    setLoading(true);
    setError(null);
    setData(null);
    depsRef.current = [];
    manifestsRef.current = [];
    resolvedCountRef.current = 0;
    lastRenderRef.current = 0;
    if (pendingRenderRef.current) {
      clearTimeout(pendingRenderRef.current);
      pendingRenderRef.current = null;
    }

    const client = api as DependencyViewerClient;

    // Check if the client supports streaming
    if (typeof client.streamDependencies !== 'function') {
      // Fallback to the original non-streaming approach
      let cancelled = false;
      api
        .getDependencies(projectSlug, manifestPaths)
        .then(response => {
          if (cancelled) return;
          setData({
            ...response,
            resolving: false,
            resolvedCount: response.dependencies.length,
          });
        })
        .catch(err => {
          if (cancelled) return;
          setError(err instanceof Error ? err : new Error(String(err)));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => { cancelled = true; };
    }

    let aborted = false;

    /**
     * Flush the current accumulated state to React.
     * Throttled to avoid excessive re-renders.
     */
    const flushToReact = (force?: boolean) => {
      if (aborted) return;

      if (pendingRenderRef.current) {
        clearTimeout(pendingRenderRef.current);
        pendingRenderRef.current = null;
      }

      const now = Date.now();
      const elapsed = now - lastRenderRef.current;

      if (!force && elapsed < RENDER_THROTTLE_MS) {
        // Schedule a deferred flush
        pendingRenderRef.current = setTimeout(() => {
          flushToReact(true);
        }, RENDER_THROTTLE_MS - elapsed);
        return;
      }

      lastRenderRef.current = now;
      const deps = depsRef.current;
      const summary = computeSummary(deps);

      setData({
        manifests: manifestsRef.current,
        dependencies: [...deps], // new array ref so React detects the change
        summary,
        resolving: true,
        resolvedCount: resolvedCountRef.current,
      });
    };

    // Use streaming
    const abort = client.streamDependencies(projectSlug, manifestPaths, {
      onManifests(event) {
        depsRef.current = [...event.dependencies];
        manifestsRef.current = event.manifests;
        resolvedCountRef.current = 0;

        const pendingSummary: DependencySummary = {
          total: event.totalCount,
          upToDate: 0,
          outdated: 0,
          unavailable: event.totalCount,
        };

        // This is the first render — always flush immediately
        lastRenderRef.current = Date.now();
        setData({
          manifests: event.manifests,
          dependencies: depsRef.current,
          summary: pendingSummary,
          resolving: true,
          resolvedCount: 0,
        });
        setLoading(false);
      },

      onBatch(event) {
        // Apply updates to the mutable deps array
        for (const update of event.updates) {
          depsRef.current[update.index] = update.dependency;
        }
        resolvedCountRef.current += event.updates.length;

        // Throttled flush to React
        flushToReact();
      },

      onDone(event) {
        // Cancel any pending throttled render
        if (pendingRenderRef.current) {
          clearTimeout(pendingRenderRef.current);
          pendingRenderRef.current = null;
        }

        const deps = [...depsRef.current];

        setData({
          manifests: manifestsRef.current,
          dependencies: deps,
          summary: event.summary,
          resolving: false,
          resolvedCount: deps.length,
        });

        // Cache the complete response
        if (typeof client.cacheResponse === 'function') {
          client.cacheResponse(projectSlug, manifestPaths, {
            manifests: manifestsRef.current,
            dependencies: deps,
            summary: event.summary,
          });
        }
      },

      onError(err) {
        setError(err);
        setLoading(false);
      },
    });

    return () => {
      aborted = true;
      abort();
      if (pendingRenderRef.current) {
        clearTimeout(pendingRenderRef.current);
        pendingRenderRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, projectSlug, pathsKey, retryCount]);

  return { data, loading, error, retry };
}

function computeSummary(deps: Dependency[]): DependencySummary {
  let upToDate = 0;
  let outdated = 0;
  let unavailable = 0;
  for (const d of deps) {
    if (d.status === 'up-to-date') upToDate++;
    else if (d.status === 'outdated') outdated++;
    else unavailable++;
  }
  return { total: deps.length, upToDate, outdated, unavailable };
}
